import React, { useEffect, useState, useCallback } from "react"; // ⚡ Added useCallback
import SplashScreen from "./pages/SplashScreen";
import MainScreen from "./pages/MainScreen";
import AdminDashboard from "./pages/AdminDashboard";
import AdminProfile from "./pages/AdminProfile";
import AdminWorkstation from "./pages/AdminWorkstation";
import AdminStaff from "./pages/AdminStaff";
import AdminProduct from "./pages/AdminProduct";
import AdminData from "./pages/AdminData";
import AdminReceive from "./pages/AdminReceive";
import AdminTreat from "./pages/AdminTreat";
import AdminPaint from "./pages/AdminPaint";
import AdminPack from "./pages/AdminPack";
import AdminDeliver from "./pages/AdminDeliver";
import AdminStock from "./pages/AdminStock";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { ToastContainer, toast } from 'react-toastify';
import { ProgressProvider } from './pages/ProgressContext';
import 'react-toastify/dist/ReactToastify.css';
import "./App.css";
import { BrowserRouter } from 'react-router-dom';

function App() {
    const [showSplash, setShowSplash] = useState(true);
    const [fadeClass, setFadeClass] = useState("fade-in");
    const [authorized, setAuthorized] = useState(false);
    const [adminEmail, setAdminEmail] = useState("");
    const [currentView, setCurrentView] = useState("dashboard");
    const [selectedFG, setSelectedFG] = useState("");
    const [selectedNode, setSelectedNode] = useState("");
    const messaging = getMessaging();

    // Debugging state changes
    useEffect(() => {
        console.log("Current View is now:", currentView);
        console.log("Selected FG is now:", selectedFG);
    }, [currentView, selectedFG]);

    // Google login
    function handleCredentialResponse(res) {
        const token = res.credential;
        const base64Url = token.split(".")[1];
        const decoded = JSON.parse(atob(base64Url));
        setAdminEmail(decoded.email);
        setAuthorized(true);
    }

    // ⚡ Fixed: Wrapped in useCallback to prevent continuous recreation loops
    const requestPermission = useCallback(() => {
        console.log('Requesting permission...');
        Notification.requestPermission().then((permission) => {
            if (permission === 'granted') {
                console.log('Notification permission granted.');
                getToken(messaging, { vapidKey: 'YOUR_PUBLIC_VAPID_KEY' })
                    .then((currentToken) => {
                        if (currentToken) {
                            console.log("Web FCM Token:", currentToken);
                        }
                    })
                    .catch((err) => console.log('An error occurred while retrieving token. ', err));
            }
        });
    }, [messaging]);

    // Foreground Notification Handler
    useEffect(() => {
        if (authorized) {
            requestPermission();

            const unsubscribe = onMessage(messaging, (payload) => {
                console.log('Foreground Message: ', payload);
                
                toast.error(`🚨 ${payload.notification.title}: ${payload.notification.body}`, {
                    position: "top-right",
                    autoClose: 10000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    navigator: true,
                });
            });

            return () => unsubscribe();
        }
    // ⚡ Fixed: Included hook dependencies safely in the array
    }, [authorized, messaging, requestPermission]);

    // Navigate to specific node detail pages from AdminProgress
    const handleProgressNavigate = (nodeName, fgNumber) => {
        if (fgNumber) setSelectedFG(fgNumber);
        setSelectedNode(nodeName);

        switch (nodeName.toLowerCase()) {
            case "receive": setCurrentView("receive"); break;
            case "treat": setCurrentView("treat"); break;
            case "paint": setCurrentView("paint"); break;
            case "pack": setCurrentView("pack"); break;
            case "deliver": setCurrentView("deliver"); break;
            default: alert(`No page for node: ${nodeName}`);
        }
    };

    // SHARED NAVIGATION ROUTER HANDLER
    const handleAlertOrStockSelection = (fg, node) => {
        console.log("!!! SYSTEM ROUTER !!! Navigating directly to target tracker module:", fg);
        setSelectedFG(fg);
        setSelectedNode(node || "");
        setCurrentView("progress");
    };

    // Google One Tap / Sign In Initializer
    useEffect(() => {
        /* global google */
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => {
            google.accounts.id.initialize({
                client_id: "YOUR_CLIENT_ID_HERE",
                callback: handleCredentialResponse,
            });
            google.accounts.id.renderButton(
                document.getElementById("googleSignInDiv"),
                { theme: "filled_blue", size: "large" }
            );
        };
        document.body.appendChild(script);
    }, []);

    // Splash fade logic
    useEffect(() => {
        const timer = setTimeout(() => {
            setFadeClass("fade-out");
            setTimeout(() => {
                setShowSplash(false);
                setFadeClass("fade-in");
            }, 1000);
        }, 3000);
        return () => clearTimeout(timer);
    }, []);

    return (
        <ProgressProvider>
            <BrowserRouter>
                <div className={`fade-container ${fadeClass}`}>
                    {showSplash ? (
                        <SplashScreen />
                    ) : authorized ? (
                        <>
                            {(currentView === "dashboard" || currentView === "progress") && (
                                <AdminDashboard
                                    email={adminEmail}
                                    sharedSelectedFg={selectedFG}        
                                    setSharedSelectedFg={setSelectedFG}
                                    externalView={currentView}
                                    setExternalView={setCurrentView}
                                    onStockClick={() => setCurrentView("stock")}
                                    onProfileClick={() => setCurrentView("profile")}
                                    onWorkstationClick={() => setCurrentView("workstation")}
                                    onStaffClick={() => setCurrentView("staff")}
                                    onProductClick={() => setCurrentView("product")}
                                    onDataClick={() => setCurrentView("data")}
                                    onProgressClick={() => setCurrentView("progress")}
                                    onNavigateToProgress={handleAlertOrStockSelection}
                                    // ⚡ Fixed: Passed unused tracking handler into Dashboard prop parameters
                                    onStepNavigation={handleProgressNavigate}
                                />
                            )}

                            {currentView === "profile" && <AdminProfile onBack={() => setCurrentView("dashboard")} email={adminEmail} />}
                            {currentView === "workstation" && <AdminWorkstation onBack={() => setCurrentView("dashboard")} email={adminEmail} />}
                            {currentView === "staff" && <AdminStaff onBack={() => setCurrentView("dashboard")} email={adminEmail} />}
                            {currentView === "product" && <AdminProduct onBack={() => setCurrentView("dashboard")} email={adminEmail} />}
                            {currentView === "data" && <AdminData onBack={() => setCurrentView("dashboard")} email={adminEmail} />}

                            {currentView === "stock" && (
                                <AdminStock
                                    key="stock-overview-main"
                                    onBack={() => setCurrentView("dashboard")}
                                    onNavigate={handleAlertOrStockSelection}
                                />
                            )}

                            {(() => {
                                const nodeComponents = {
                                    receive: AdminReceive,
                                    treat: AdminTreat,
                                    paint: AdminPaint,
                                    pack: AdminPack,
                                    deliver: AdminDeliver,
                                };
                                const NodeComp = nodeComponents[currentView];
                                if (NodeComp) {
                                    return (
                                        <NodeComp
                                            onBack={() => setCurrentView("progress")}
                                            fgNumber={selectedFG}
                                            nodeName={selectedNode}
                                        />
                                    );
                                }
                                return null;
                            })()}
                            
                            <ToastContainer />
                        </>
                    ) : (
                        <div style={{ width: "100%" }}>
                            <MainScreen onAdminAuthorized={(email) => { setAdminEmail(email); setAuthorized(true); }} />
                            <div id="googleSignInDiv" style={{ marginTop: 0, textAlign: "center" }}></div>
                        </div>
                    )}
                </div>
            </BrowserRouter>
        </ProgressProvider>
    );
}

export default App;